import moment from 'moment';
import { RequestScheduleOverTimeDetail } from 'hooks/useRoiRequestScheduleOverTime';
import { DATE_TIME_FORMAT } from './datetime';

export type Granularity = 'day' | 'week' | 'month' | 'year';

export const LABEL_FORMAT_BY_GRANULARITY: Record<Granularity, string> = {
  day: 'ddd D',
  week: 'D MMM YYYY',
  month: 'MMM YYYY',
  year: 'YYYY',
};

const DAY_THRESHOLD = 31;
const WEEK_THRESHOLD = 120;
const MONTH_THRESHOLD_YEARS = 3;

export function getGranularityForRange(
  fromDate: string,
  toDate: string
): Granularity {
  if (!fromDate || !toDate) return 'day';

  const start = moment(fromDate);
  const end = moment(toDate);
  const days = end.diff(start, 'days');

  if (days <= DAY_THRESHOLD) return 'day';
  if (days <= WEEK_THRESHOLD) return 'week';
  if (days <= MONTH_THRESHOLD_YEARS * 365) return 'month';
  return 'year';
}

export function bucketTimeSeries<T extends { date: string }>(
  entries: T[],
  granularity: Granularity,
  sumKeys: (keyof T)[]
): T[] {
  const data = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  if (granularity === 'day') {
    return data;
  }

  const buckets = new Map<string, T>();

  for (const entry of data) {
    const key = getBucketKey(entry.date, granularity);
    const existing = buckets.get(key);

    if (existing) {
      for (const sumKey of sumKeys) {
        const sum =
          (existing[sumKey] as unknown as number) +
          (entry[sumKey] as unknown as number);
        existing[sumKey] = sum as unknown as T[keyof T];
      }
    } else {
      buckets.set(key, { ...entry, date: key });
    }
  }

  return Array.from(buckets.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
}

export function bucketRequestsSchedule(
  entries: RequestScheduleOverTimeDetail[],
  granularity: Granularity
): RequestScheduleOverTimeDetail[] {
  return bucketTimeSeries(entries, granularity, [
    'firstInLinks',
    'googleBookOnline',
    'others',
    'totalRequests',
  ]);
}

function getBucketKey(date: string, granularity: Granularity): string {
  const m = moment(date);
  if (granularity === 'week')
    return m.startOf('isoWeek').format(DATE_TIME_FORMAT.APPOINTMENT_DATE);
  if (granularity === 'month')
    return m.startOf('month').format(DATE_TIME_FORMAT.APPOINTMENT_DATE);
  if (granularity === 'year')
    return m.startOf('year').format(DATE_TIME_FORMAT.APPOINTMENT_DATE);
  return m.format(DATE_TIME_FORMAT.APPOINTMENT_DATE);
}
